// The questions a deep run's research threads are exploring, in plain English, for the run status
// panel. A thread's brief is written for a research agent ("Attack the NO case for…"), so Sayer's
// fast model rewords new briefs as questions, a batch at a time; until it answers, or if it
// fails, the brief is reworded without it (lib/forecast/deepProgress.js plainQuestion).
import { useEffect, useMemo, useReducer } from "react";
import { callLlm } from "../lib/forecast/llmClient.js";
import {
  briefKey,
  decodeBriefs,
  encodeBriefs,
  mentionsForecaster,
  parseQuestions,
  plainQuestion,
  questionsPrompt,
} from "../lib/forecast/deepProgress.js";

const REWORD_TIMEOUT_MS = 20000;
// Reworded briefs for the page's life, by forecast question and brief (briefKey): a brief is
// worded once per forecast, however often the panel reopens.
const WORDED = new Map();
const ASKING = new Set();

/**
 * @param {object} deps
 * @param {readonly {callId: string, mission: string, done: boolean}[]} deps.threads the threads to list
 * @param {string} deps.question the forecast's question, for context
 * @param {boolean} deps.enabled whether the panel is showing them
 * @returns {{callId: string, text: string, done: boolean}[]}
 */
export function useResearchQuestions({ threads, question, enabled }) {
  const [worded, bump] = useReducer((n) => n + 1, 0);
  const missions = enabled ? [...new Set(threads.map((t) => t.mission))] : [];
  const pending = encodeBriefs(
    missions.filter(
      (m) => !WORDED.has(briefKey(question, m)) && !ASKING.has(briefKey(question, m)),
    ),
  );

  useEffect(() => {
    const batch = decodeBriefs(pending);
    if (!batch.length) return;
    batch.forEach((m) => ASKING.add(briefKey(question, m)));
    callLlm(questionsPrompt(question, batch), { webSearch: false, timeoutMs: REWORD_TIMEOUT_MS })
      .then((reply) => {
        const parsed = parseQuestions(reply, batch.length);
        batch.forEach((m, i) => {
          const text = parsed?.[i];
          WORDED.set(
            briefKey(question, m),
            text && !mentionsForecaster(text) ? text : plainQuestion(m),
          );
        });
      })
      .catch(() => batch.forEach((m) => WORDED.set(briefKey(question, m), plainQuestion(m))))
      .finally(() => {
        batch.forEach((m) => ASKING.delete(briefKey(question, m)));
        bump();
      });
  }, [pending, question]);

  return useMemo(
    () =>
      enabled
        ? threads
            .map((t) => ({
              callId: t.callId,
              text: WORDED.get(briefKey(question, t.mission)) ?? plainQuestion(t.mission),
              done: t.done,
            }))
            // A question that still talks about the research itself is left out.
            .filter((q) => q.text && !mentionsForecaster(q.text))
        : [],
    // `worded` re-reads the module cache once a batch lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threads, enabled, question, worded],
  );
}
